import { useState } from 'react';
import { Button } from '@/shared/components';

/**
 * Letzter Schritt der Einrichtung: die Wiederherstellungscodes. Der Server zeigt sie nur dieses
 * eine Mal, deshalb geht es erst weiter, wenn der Haken sagt, dass sie gesichert sind.
 */
export function RecoveryCodesPanel({
  codes,
  onDone,
  doneLabel = 'Fertig',
  loading = false,
  failure = null,
}: {
  codes: string[];
  onDone: () => void;
  doneLabel?: string;
  loading?: boolean;
  /** Fehler beim Abschließen als Satz; die Codes bleiben dabei stehen. */
  failure?: string | null;
}) {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <section className="border-hairline rounded-tile flex flex-col gap-3 border bg-white p-5">
      <b className="font-serif text-xl">Wiederherstellungscodes sichern</b>
      <p className="text-ink-soft text-sm leading-relaxed">
        Jeder Code funktioniert einmal, falls dein Handy fehlt. Bewahr sie getrennt von diesem Gerät
        auf – nach dem Verlassen erscheinen sie nicht noch einmal.
      </p>
      <ul
        aria-label="Wiederherstellungscodes"
        className="bg-card-alt rounded-control grid grid-cols-2 gap-x-4 gap-y-1.5 p-4 font-mono text-sm"
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" className="min-h-11" onClick={() => void copy()}>
          {copied === 'ok' ? 'Kopiert' : 'Alle kopieren'}
        </Button>
        {copied === 'failed' && (
          <span className="text-ink-soft text-xs">Kopieren ging nicht – schreib sie ab.</span>
        )}
      </div>
      {failure && (
        <p
          role="alert"
          className="bg-sealwax-tint text-sealwax rounded-control p-3 text-sm font-semibold"
        >
          {failure}
        </p>
      )}
      <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-semibold">
        <input
          type="checkbox"
          className="accent-forest size-5"
          checked={confirmed}
          disabled={loading}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        Ich habe die Codes sicher gespeichert.
      </label>
      <Button
        variant="brand"
        size="lg"
        className="w-full sm:w-auto sm:self-start"
        disabled={!confirmed || loading}
        loading={loading}
        onClick={onDone}
      >
        {doneLabel}
      </Button>
    </section>
  );
}
